import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import EditJobModal from "./EditJobModal";
// Import centralized date utilities
import { formatDateForTable } from "../../utils/dateUtils";
import { hasAnyEditPermission } from './utils/schedulePermissions';
// Import currency formatting
import { formatCurrency } from './utils/tableUtils';
import moment from 'moment';
import { getProjectStatus } from '../../utils/dataNormalization';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { JOB_TYPE_OPTIONS, getJobTypeFromJobNumber } from './utils/jobTypeUtils';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";

const ShippedJobs = ({ filteredJobs = [], canEdit = true, searchTerm = '' }) => {
  const dispatch = useDispatch();
  // Use filteredJobs prop if available, otherwise fall back to Redux store
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const apiUrls = useSelector(selectApiUrls);
  const [shippedApiJobs, setShippedApiJobs] = useState([]);
  const [loadingShippedJobs, setLoadingShippedJobs] = useState(false);
  const [shippedJobsError, setShippedJobsError] = useState(null);
  
  // Get user roles for permission checking
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  const canEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState("All");
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [showCompactFilters, setShowCompactFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const containerRef = useRef(null);

  useScheduleRealtime({ view: 'shipped', blocked: loadingShippedJobs || showModal, apply: rows => { setShippedApiJobs(rows); setShippedJobsError(null); } });

  useEffect(() => {
    let isMounted = true;

    const loadShippedJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingShippedJobs(true);
      setShippedJobsError(null);

      try {
        const url = `${apiUrls.fullSchedule}?jobStatus=Shipped`;
        const response = await dispatch(fetchData({ url })).unwrap();
        const raw = response?.data || response || {};
        const normalizedJobs = Array.isArray(raw)
          ? raw
          : Object.values(raw)
            .map((item) => item?.GeneralProjectInfo)
            .filter(Boolean);

        if (isMounted) {
          setShippedApiJobs(normalizedJobs);
        }
      } catch (error) {
        console.error('ShippedJobs: Failed to load shipped jobs from full schedule endpoint:', error);
        if (isMounted) {
          setShippedJobsError('Failed to load shipped jobs.');
        }
      } finally {
        if (isMounted) {
          setLoadingShippedJobs(false);
        }
      }
    };

    loadShippedJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  const handleRowDoubleClick = (job) => {
    // Disable double-click functionality for accounting users (shipped jobs are read-only for accounting)
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      return; // Do nothing for accounting users
    }
    
    // Only allow double-click functionality if user has edit permissions
    if (canEditAccess) {
      setSelectedJob(job);
      setShowModal(true);
    }
    // Do nothing if the user doesn't have permissions
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleSaveChanges = () => {
    console.log("Job updated successfully");
    handleCloseModal();
  };

  // Helper function to format assigned engineers
  const formatAssignedEngineers = (job) => {
    const engineers = [];
    
    if (job.MechanicalEngineer && job.MechanicalEngineer.trim() !== "") {
      engineers.push(`M: ${job.MechanicalEngineer}`);
    }
    
    if (job.ElectricalEngineer && job.ElectricalEngineer.trim() !== "") {
      engineers.push(`E: ${job.ElectricalEngineer}`);
    }
    
    if (job.ControlsEngineer && job.ControlsEngineer.trim() !== "") {
      engineers.push(`C: ${job.ControlsEngineer}`);
    }
    
    return engineers.length > 0 ? engineers.join(" | ") : "—";
  };

  const tableColumns = [
    { key: "JobNumber", label: "Job Number", type: "text" },
    { key: "ProjectName", label: "Project Name", type: "text" },
    { key: "ModelNumber", label: "Model Number", type: "text" },
    { key: "OrderDate", label: "Order Date", type: "date" },
    { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
    { key: "RequestedDeliveryDate", label: "Requested Delivery Date", type: "date" },
    { key: "SalesRep", label: "Sales Rep", type: "text" },
    { key: "State", label: "State", type: "text" },
    { key: "JobType", label: "Job Type", type: "text" },
    {
      key: "AssignedEngineers",
      label: "Assigned Engineers",
      type: "text",
      getValue: formatAssignedEngineers,
    },
    { key: "ActualShipDate", label: "Actual Ship Date", type: "date" },
    { key: "Notes", label: "Notes", type: "text" },
    { key: "DollarValue", label: "Dollar Value", type: "currency" },
  ];

  const hasActiveSearch = typeof searchTerm === 'string' && searchTerm.trim() !== '';

  const handleJobTypeFilterToggle = (jobType, isChecked) => {
    setSelectedJobTypes((currentJobTypes) => (
      isChecked
        ? [...new Set([...currentJobTypes, jobType])]
        : currentJobTypes.filter((currentJobType) => currentJobType !== jobType)
    ));
  };

  // Match the global search behavior for key identifiers.
  const matchesSearchTerm = (job, term) => {
    const lowerTerm = term.toLowerCase().trim();
    if (!lowerTerm) {
      return true;
    }

    const jobNumber = (job?.JobNumber || '').toString().toLowerCase();
    const projectName = (job?.ProjectName || '').toString().toLowerCase();
    const modelNumber = (job?.ModelNumber || '').toString().toLowerCase();

    return (
      jobNumber.includes(lowerTerm) ||
      projectName.includes(lowerTerm) ||
      modelNumber.includes(lowerTerm)
    );
  };

  // Use active search against shipped API jobs when available; otherwise preserve existing fallbacks.
  const dataToUse = useMemo(() => {
    if (hasActiveSearch) {
      const searchSources = [
        Array.isArray(filteredJobs) ? filteredJobs : [],
        Array.isArray(shippedApiJobs) ? shippedApiJobs : [],
        Array.isArray(jobScheduleData) ? jobScheduleData : [],
      ];

      for (const source of searchSources) {
        if (source.length === 0) {
          continue;
        }

        const matched = source.filter((job) => matchesSearchTerm(job, searchTerm));
        if (matched.length > 0) {
          return matched;
        }
      }

      return [];
    }

    if (shippedApiJobs.length > 0) {
      return shippedApiJobs;
    }

    return filteredJobs.length > 0 ? filteredJobs :
      (Array.isArray(jobScheduleData) ? jobScheduleData : []);
  }, [hasActiveSearch, shippedApiJobs, filteredJobs, jobScheduleData, searchTerm]);
  
  const shippedJobsForFilters = useMemo(() => (
    dataToUse.filter((job) =>
      getProjectStatus(job) === "Shipped" && 
      job.ActualShipDate !== null && 
      job.ActualShipDate !== undefined && 
      job.ActualShipDate !== ""
    )
  ), [dataToUse]);

  const jobTypeFilteredJobsForDropdowns = useMemo(() => {
    if (selectedJobTypes.length === 0) {
      return shippedJobsForFilters;
    }

    return shippedJobsForFilters.filter((job) => {
      const jobType = getJobTypeFromJobNumber(job.JobNumber);
      return jobType && selectedJobTypes.includes(jobType);
    });
  }, [shippedJobsForFilters, selectedJobTypes]);
  
  // Get unique sales reps from shipped jobs for filter dropdown
  const uniqueSalesReps = useMemo(() => {
    const shippedJobs = jobTypeFilteredJobsForDropdowns;
    
    const salesReps = [...new Set(shippedJobs.map(job => job.SalesRep))]
      .filter(rep => rep && rep.trim() !== "")
      .sort();
    
    return ["All", ...salesReps];
  }, [jobTypeFilteredJobsForDropdowns]);
  
  // Filter for shipped jobs: (ProjectStatus = "Shipped" OR JobStatus = "Shipped") AND contains an Actual Ship Date
  const filteredShippedJobs = dataToUse
    .filter((job) => 
      getProjectStatus(job) === "Shipped" && 
      job.ActualShipDate !== null && 
      job.ActualShipDate !== undefined && 
      job.ActualShipDate !== "" &&
      (selectedJobTypes.length === 0 || selectedJobTypes.includes(getJobTypeFromJobNumber(job.JobNumber))) &&
      // Filter by selected sales rep
      (selectedSalesRep === "All" || job.SalesRep === selectedSalesRep)
    )
    // Sort by Actual Ship Date in descending order (most recent first)
    .sort((a, b) => {
      const dateA = new Date(a.ActualShipDate);
      const dateB = new Date(b.ActualShipDate);
      
      // Handle invalid dates
      if (!dateA || isNaN(dateA.getTime())) return 1;
      if (!dateB || isNaN(dateB.getTime())) return -1;
      
      return dateB.getTime() - dateA.getTime();
    });

  const sortedShippedJobs = sortTableRows(
    filteredShippedJobs,
    tableColumns,
    sortConfig
  );

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  // Group jobs by month and calculate totals
  const { groupedJobsByMonth, sortedMonths } = useMemo(() => {
    const grouped = {};
    
    sortedShippedJobs.forEach(job => {
      if (job.ActualShipDate) {
        const shipDate = moment(job.ActualShipDate);
        const monthKey = shipDate.format('YYYY-MM');
        const monthLabel = shipDate.format('MMMM YYYY');
        
        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            label: monthLabel,
            jobs: []
          };
        }
        grouped[monthKey].jobs.push(job);
      }
    });
    
    // Sort months in descending order (most recent first)
    const sorted = Object.keys(grouped).sort((a, b) => moment(b).diff(moment(a)));
    
    return { 
      groupedJobsByMonth: grouped, 
      sortedMonths: sorted 
    };
  }, [sortedShippedJobs]);

  // Calculate month total dollar value
  const calculateMonthTotalDollarValue = (monthJobs) => {
    const totalDollarValue = monthJobs.reduce((sum, job) => {
      const dollarValue = job.DollarValue;
      let numValue = 0;
      
      if (typeof dollarValue === "number") {
        numValue = dollarValue;
      } else if (typeof dollarValue === "string" && dollarValue.trim() !== "") {
        // Convert string to number, removing any currency formatting
        const cleaned = dollarValue.replace(/[^0-9.-]/g, '');
        numValue = parseFloat(cleaned);
        if (isNaN(numValue)) numValue = 0;
      }
      
      return sum + numValue;
    }, 0);
    
    return formatCurrency(totalDollarValue);
  };

  // Apply virtual scrolling logic to individual month jobs
  const getVisibleJobsForMonth = (monthJobs) => {
    // For now, we'll show all jobs in each month, but this could be optimized later
    return monthJobs;
  };

  return (
    <div
      className="shipped-jobs-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      {shippedJobsError && (
        <Alert variant="warning" className="m-3 mb-0">
          {shippedJobsError}
        </Alert>
      )}

      {loadingShippedJobs && (
        <div className="m-3 mb-0 d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading shipped jobs...</span>
        </div>
      )}

      {/* Filter Controls */}
      <Button
        variant="outline-primary"
        className="schedule-filter-toggle mb-3"
        onClick={() => setShowCompactFilters(isOpen => !isOpen)}
        aria-expanded={showCompactFilters}
        aria-controls="shipped-jobs-filters"
      >
        <i className="bi bi-funnel me-2"></i>
        Filters
      </Button>
      <div
        id="shipped-jobs-filters"
        className={`controls-section schedule-filter-panel ${showCompactFilters ? 'is-open' : ''}`}
        style={{ padding: "1rem", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6", marginBottom: "1rem" }}
      >
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Filter by Sales Rep:</Form.Label>
              <Form.Select
                value={selectedSalesRep}
                onChange={(e) => setSelectedSalesRep(e.target.value)}
                size="sm"
              >
                {uniqueSalesReps.map((rep) => (
                  <option key={rep} value={rep}>
                    {rep}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Job Type</Form.Label>
              <div className="border rounded p-2 bg-white" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                {JOB_TYPE_OPTIONS.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`shippedJobType-${option.value}`}
                    label={option.label}
                    checked={selectedJobTypes.includes(option.value)}
                    onChange={(e) => handleJobTypeFilterToggle(option.value, e.target.checked)}
                    className="mb-1"
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
        </Row>
      </div>
      
      <Table striped bordered hover responsive className="schedule-responsive-table">
        <SortableTableHeader
          columns={tableColumns}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <tbody>
          {sortedMonths.length > 0 ? (
            sortedMonths.map((monthKey) => {
              const monthData = groupedJobsByMonth[monthKey];
              const monthJobs = monthData.jobs;
              const visibleMonthJobs = getVisibleJobsForMonth(monthJobs);
              const monthTotal = calculateMonthTotalDollarValue(monthJobs);
              
              return (
                <React.Fragment key={monthKey}>
                  {/* Month separator header */}
                  <tr className="month-separator schedule-section-row" style={{ pageBreakInside: 'avoid' }}>
                    <td 
                      colSpan="13" 
                      className="month-header"
                      style={{ 
                        backgroundColor: '#0055a5',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '12pt',
                        padding: '8px'
                      }}
                    >
                      {monthData.label} - Shipped: {monthJobs.length} - Total: {monthTotal}
                    </td>
                  </tr>

                  {/* Job rows for this month */}
                  {visibleMonthJobs.map((job) => (
                    <tr
                      key={job.ID}
                      onDoubleClick={() => handleRowDoubleClick(job)}
                      style={{ cursor: (canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling)) ? 'pointer' : 'default' }}
                      title={(canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling)) ? "Double-click to edit" : ""}
                    >
                      <td data-label="Job Number">
                        {job.ID && canOpenPortalJob(job) ? (
                          <a
                            href={`/${job.ID}/generalInfo`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {job.JobNumber}
                          </a>
                        ) : (
                          job.JobNumber
                        )}
                      </td>
                      <td data-label="Project Name">{job.ProjectName}</td>
                      <td data-label="Model Number">{job.ModelNumber}</td>
                      <td data-label="Order Date">{formatDateForTable(job.OrderDate)}</td>
                      <td data-label="Production Release">{formatDateForTable(job.ProductionRelease)}</td>
                      <td data-label="Requested Delivery">{formatDateForTable(job.RequestedDeliveryDate)}</td>
                      <td data-label="Sales Rep">{job.SalesRep}</td>
                      <td data-label="State">{job.State}</td>
                      <td data-label="Job Type">{job.JobType}</td>
                      <td data-label="Assigned Engineers" style={{ fontSize: '0.85rem' }}>{formatAssignedEngineers(job)}</td>
                      <td data-label="Actual Ship Date">{formatDateForTable(job.ActualShipDate)}</td>
                      <td data-label="Notes">{job.Notes}</td>
                      <td data-label="Dollar Value" style={{ textAlign: 'right' }}>{formatCurrency(job.DollarValue)}</td>
                      {canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) && (
                        <td data-label="Actions" className="schedule-card-action-cell">
                          <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                            Open job
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan="13" className="schedule-empty-row">No data available</td>
            </tr>
          )}
        </tbody>
      </Table>
      <Modal className="job-schedule-modal" show={showModal} onHide={handleCloseModal} size="xl">
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseModal}
          handleSaveChanges={handleSaveChanges}
          userRoles={userRoles}
        />
      </Modal>
    </div>
  );
};

export default ShippedJobs;
